/**
 * Unified summary cards showing key metrics with period comparison
 */
export default function MetricsSummary({ summary, previousSummary, dateRangeLabel, comparisonLabels }) {
  if (!summary) return null;

  const hasComparison = previousSummary != null;

  // Calculate percentage change between two values
  const calculateChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const allCards = [
    {
      label: 'Total Órdenes',
      subtitle: 'Pasteles + roscas (uds.) + panes + merchant + especiales',
      value: summary.totalOrders,
      previous: previousSummary?.totalOrders || 0,
      color: '#1976d2',
      alwaysShow: true,
    },
    {
      label: 'Pasteles',
      subtitle: 'Pedidos de pastel',
      value: summary.totalCakes,
      previous: previousSummary?.totalCakes || 0,
      color: '#c43b21',
    },
    {
      label: 'Roscas (uds.)',
      subtitle: 'Unidades vendidas, no pedidos',
      value: summary.totalRoscas,
      previous: previousSummary?.totalRoscas || 0,
      color: '#7b1fa2',
    },
    {
      label: 'Panes',
      subtitle: 'Pedidos de pan',
      value: summary.totalBreads,
      previous: previousSummary?.totalBreads || 0,
      color: '#d4a574',
    },
    {
      label: 'Merchant',
      subtitle: 'Pedidos de mayoreo',
      value: summary.totalMerchant,
      previous: previousSummary?.totalMerchant || 0,
      color: '#0d8742',
    },
    {
      label: 'Especiales',
      subtitle: 'Pedidos especiales',
      value: summary.totalCustom,
      previous: previousSummary?.totalCustom || 0,
      color: '#c98700',
    },
    {
      label: 'Promedio/Día',
      subtitle: 'Total ÷ dias en el rango',
      value: summary.avgOrdersPerDay,
      color: '#6c6c7b',
      alwaysShow: true,
      noComparison: true,
    },
    {
      label: 'En Línea',
      subtitle: 'Sin cajero asignado',
      value: summary.totalOnline || 0,
      previous: previousSummary?.totalOnline || 0,
      color: '#2196F3',
      channelCard: true,
    },
    {
      label: 'En Tienda',
      subtitle: 'Con cajero asignado',
      value: summary.totalInPerson || 0,
      previous: previousSummary?.totalInPerson || 0,
      color: '#4CAF50',
      channelCard: true,
    },
  ];

  // Add merchant revenue if there's revenue data
  if (summary.totalRevenue > 0) {
    allCards.push({
      label: 'Ingresos Merchant',
      value: `$${summary.totalRevenue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
      color: '#0d8742',
      isRevenue: true,
      noComparison: true,
    });
  }

  // Filter out cards with 0 value (unless alwaysShow)
  const cards = allCards.filter(card => card.alwaysShow || card.value > 0 || card.previous > 0);

  const ChangeIndicator = ({ value }) => {
    if (value === 0) return <span className="change-indicator neutral">0%</span>;
    const isPositive = value > 0;
    return (
      <span className={`change-indicator ${isPositive ? 'positive' : 'negative'}`}>
        {isPositive ? '+' : ''}{value}%
      </span>
    );
  };

  return (
    <div className="metrics-summary">
      <div className="metrics-summary-header">
        <h3>Resumen</h3>
        <div className="metrics-summary-labels">
          <span className="date-range-label">{dateRangeLabel}</span>
          {hasComparison && comparisonLabels && (
            <span className="comparison-label-hint">vs. mismo periodo año pasado ({comparisonLabels.previousLabel})</span>
          )}
        </div>
      </div>
      <div className="metrics-cards-grid">
        {cards.map((card, index) => {
          // Skip comparison for channel cards when previous data doesn't exist
          const noPreviousData = card.channelCard && (card.previous || 0) === 0;
          const change = !card.noComparison && hasComparison && !noPreviousData
            ? calculateChange(card.value || 0, card.previous || 0)
            : null;

          return (
            <div
              key={index}
              className="metric-card"
              style={{ borderLeftColor: card.color }}
            >
              <div className="metric-card-header">
                <span className="metric-label">{card.label}</span>
                {change !== null && <ChangeIndicator value={change} />}
              </div>
              {card.subtitle && (
                <span className="metric-subtitle">{card.subtitle}</span>
              )}
              <span className="metric-value" style={{ color: card.color }}>
                {card.isRevenue ? card.value : card.value.toLocaleString()}
              </span>
              {!card.noComparison && hasComparison && (
                noPreviousData
                  ? <span className="metric-previous">Sin datos del año pasado</span>
                  : <span className="metric-previous">vs {card.previous.toLocaleString()}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
